<template>
	<nta-app-layout
		:offline="isOffline"
		:not-found="isNotFound"
		:loading="isLoading"
		:router="router"
		:symbol-lang="domainStore.languagePreference"
	>
		<nta-sso v-if="!isAuthenticated" @authenticated="onAuthenticated" />
		<template #navigation>
			<div
				v-if="isEditorView && displayResponsive"
				style="
					background: rgb(var(--v-theme-primary));
					color: rgb(var(--v-theme-on-primary));
					padding: 4px;
					display: flex;
					justify-content: center;
				"
			>
				<nta-cube
					:size="40"
					:active="['operator', 'designer', 'engineer']"
					style="filter: drop-shadow(0px 2px 8px rgba(0, 0, 0, 0.6))"
					@click="router.back()"
				/>
			</div>
			<Navigation v-else-if="!hideNav" />
			<div v-else></div>
		</template>
		<router-view v-if="isAuthenticated"></router-view>
		<RestartNotification />
	</nta-app-layout>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed } from "vue";
import Navigation from "./components/Navigation.vue";
import RestartNotification from "./components/RestartNotification.vue";
import router from "./router";
import { useDomainStore } from "./store/domain";
import { useGraphStore } from "./store/graph";
import { useInstanceDataspacesStore } from "./store/i-dataspace";
import { useInstanceWorkspacesStore } from "./store/i-workspace";
import { useModelGraphStore } from "./store/modelGraph";
import { useBetaStore } from "./store/beta";
import { useAudioStore } from "./store/alternis/audio";
import { useConfigStore } from "./store/alternis/config";
import { useSymbolsStore } from "./store/symbols";
import { useDebugStore } from "./store/debug";
import { useUserStore } from "./store/user";
import { api, apiConfig as operatorAPIConfig } from "@lr-notitia/notitia-operator/client";
import { apiConfig as alternisAPIConfig } from "@lr-notitia/notitia-alternis/client";
import { apiConfig as DictionaryAPIConfig } from "@lr-notitia/notitia-dictionary/client";
import { useResponsiveLayout } from "@lr-notitia/notitia-ui";

operatorAPIConfig.host = window.location.href.includes("localhost") ? import.meta.env.VITE_API_HOST || "" : "";
DictionaryAPIConfig.host = "https://dictionary.notitia.world";

const isAuthenticated = ref(false);
const isOffline = ref(false);
const isNotFound = ref(false);
const isLoading = ref(false);

let resolveAuth: (value: unknown) => void;
const authentification = new Promise((resolve) => {
	resolveAuth = resolve;
});
function onAuthenticated(data: any) {
	operatorAPIConfig.authToken = data.token;
	alternisAPIConfig.authToken = data.token;
	DictionaryAPIConfig.authToken = data.token;
	domainStore.authToken = data.token;
	userStore.currentUser = data.data;
	resolveAuth(data);
}

const domainId = ref<string | null>(null);
const dataspaceId = ref<string | null>(null);
const workspaceId = ref<string | null>(null);
const domainStore = useDomainStore();
const graphStore = useGraphStore();
const modelStore = useModelGraphStore();
const instanceDataspacesStore = useInstanceDataspacesStore();
const instanceWorkspacesStore = useInstanceWorkspacesStore();
const betaStore = useBetaStore();
const audioStore = useAudioStore();
const configStore = useConfigStore();
const symbolsStore = useSymbolsStore();
const debugStore = useDebugStore();
const userStore = useUserStore();

alternisAPIConfig.host = configStore.ALTERNIS_HOST;

const hideNav = computed(() => {
	if (router.currentRoute.value.name === "Workspace Editor") return true;

	if (router.currentRoute.value.name === "Messe") return true;
	if (router.currentRoute.value.name === "Berufsberatung") return true;
	if (router.currentRoute.value.name === "ITNotruf") return true;
	if (router.currentRoute.value.name === "Krankentransport Landing Page") return true;
	if (router.currentRoute.value.name === "Krankentransport Saarland") return true;
	if (router.currentRoute.value.name === "Krankentransport Freiburg/Hochschwarzwald") return true;
	if (router.currentRoute.value.name === "Henkel") return true;
	if (router.currentRoute.value.name === "evolving systems consulting") return true;
	if (router.currentRoute.value.name === "iPad") return true;
	if (router.currentRoute.value.name === "iPad Demo") return true;
	if (router.currentRoute.value.name === "Boehringer") return true;
	if (router.currentRoute.value.name === "Ansbach") return true;
	return false;
});

const isEditorView = computed(() => {
	if (router.currentRoute.value.name == "Workspace Editor") return true;
	return false;
});

const { displayResponsive } = useResponsiveLayout();

onMounted(async () => {
	await authentification;
	await betaStore.getKinasBetaDataspaceId().catch((err) => console.error(err));
	isAuthenticated.value = true;
	await symbolsStore.loadSymbols();
	api()
		.socket()
		.onDisconnect(() => {
			isOffline.value = true;
		});
	api()
		.socket()
		.onReconnect(() => {
			window.location.reload();
		});
});

window.addEventListener("offline", () => {
	isOffline.value = true;
});
window.addEventListener("online", () => {
	if (isOffline.value) {
		window.location.reload();
	}
});

router.afterEach(async (to) => {
	await authentification;

	domainId.value = (router.currentRoute.value.params.domainId as string) || null;

	workspaceId.value = (router.currentRoute.value.params.workspaceId as string) || null;
	if (workspaceId.value) instanceWorkspacesStore.openedWorkspaceId = workspaceId.value;
	else instanceWorkspacesStore.openedWorkspaceId = undefined;

	dataspaceId.value = (router.currentRoute.value.params.dataspaceId as string) || null;
	if (dataspaceId.value) instanceDataspacesStore.openedDataspaceId = dataspaceId.value;
	else instanceDataspacesStore.openedDataspaceId = undefined;

	if (!domainStore.account.role && domainId.value) await domainStore.loadDomain(domainId.value);
	debugStore.setAltKeyHeld(false);
	graphStore.descriptor.hiddenNotifications = [];
});

watch(
	() => domainId.value,
	async () => {
		await authentification;
		if (domainId.value) {
			isLoading.value = true;
			await domainStore.loadDomain(domainId.value).catch(() => (isNotFound.value = true));
			await modelStore.loadGraph(domainId.value);
			await instanceDataspacesStore.loadDataspaces(domainId.value);
			if (workspaceId.value) {
				await graphStore.loadGraph(domainId.value, workspaceId.value).catch(() => (isNotFound.value = true));
			}
			isLoading.value = false;
		} else {
			domainStore.clearDomain();
			instanceDataspacesStore.clearDataspaces();
			modelStore.clearGraph();
		}
	},
	{ immediate: true },
);
watch(
	() => dataspaceId.value,
	async () => {
		await authentification;
		if (domainId.value) await instanceDataspacesStore.loadDataspaces(domainId.value);
		else instanceWorkspacesStore.clearWorkspaces();
	},
);
watch(
	() => workspaceId.value,
	async (newId, oldId) => {
		await authentification;
		// debugStore.debugMode = false;
		audioStore.stopAll();
		if (oldId) {
			graphStore.clearGraph(oldId);
		}
		if (newId && domainId.value) {
			graphStore.loadGraph(domainId.value, newId).catch(() => (isNotFound.value = true));
		}
	},
);
</script>

<style lang="scss">
#app {
	overflow: hidden;
}
.v-main {
	overflow: hidden;
}
</style>
